import React from 'react';
import { useSearchableDropdown } from '../../hooks/useSearchableDropdown';

export interface SearchableDropdownProps<T> {
  items: T[];
  filterFn: (item: T, query: string) => boolean;
  getDisplayValue: (item: T) => string;
  getPrimaryLabel?: (item: T) => React.ReactNode;
  getSecondaryLabel?: (item: T) => React.ReactNode;
  onSelect: (item: T) => void;
  onInputChange?: (value: string) => void;
  onFocus?: () => void;
  label?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  initialValue?: string;
  className?: string;
  inputClassName?: string;
  dropdownClassName?: string;
  emptyMessage?: string;
  required?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}

/**
 * Reusable searchable dropdown component.
 * Provides a unified interface for dropdown searches across the application.
 */
export function SearchableDropdown<T>({
  items,
  filterFn,
  getDisplayValue,
  getPrimaryLabel,
  getSecondaryLabel,
  onSelect,
  onInputChange: onInputChangeExternal,
  onFocus: onFocusExternal,
  label,
  placeholder = 'Search...',
  disabled = false,
  initialValue = '',
  className = '',
  inputClassName = '',
  dropdownClassName = '',
  emptyMessage = 'No items found',
  required = false,
  inputRef,
}: SearchableDropdownProps<T>) {
  const {
    searchQuery,
    showDropdown,
    filteredItems,
    highlightedIndex,
    handleItemSelect,
    handleInputChange,
    handleInputFocus,
    handleKeyDown,
    containerRef,
  } = useSearchableDropdown({
    items,
    filterFn,
    getDisplayValue,
    onSelect,
    disabled,
    initialValue,
  });

  const handleInputChangeWithCallback = (value: string) => {
    handleInputChange(value);
    onInputChangeExternal?.(value);
  };

  const handleFocusWithCallback = () => {
    handleInputFocus();
    onFocusExternal?.();
  };

  const displayPrimaryLabel = getPrimaryLabel || getDisplayValue;
  const displaySecondaryLabel = getSecondaryLabel;

  return (
    <div className={`relative ${className}`} ref={containerRef} data-dropdown-open={showDropdown}>
      {label && (
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <input
        ref={inputRef}
        type="text"
        value={searchQuery}
        onChange={(e) => handleInputChangeWithCallback(e.target.value)}
        onFocus={handleFocusWithCallback}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder={placeholder}
        className={`w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 ${inputClassName}`}
      />
      {showDropdown && !disabled && (
        <div
          className={`absolute z-50 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto ${dropdownClassName}`}
        >
          {filteredItems.length > 0 ? (
            filteredItems.map((item, index) => (
              <div
                key={index}
                onClick={() => handleItemSelect(item)}
                className={`px-4 py-2 cursor-pointer border-b border-gray-100 dark:border-gray-600 last:border-b-0 ${
                  index === highlightedIndex 
                    ? 'bg-blue-100 dark:bg-gray-500' 
                    : 'hover:bg-blue-50 dark:hover:bg-gray-600'
                }`}
              >
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  {displayPrimaryLabel(item)}
                </div>
                {displaySecondaryLabel && (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {displaySecondaryLabel(item)}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
              {emptyMessage}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchableDropdown;
